import React, { useMemo } from "react";
import "../LineChart/linechart.css";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
 
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-green-600 text-white px-4 py-2 rounded-lg shadow-md text-center customtooltip">
        <p className="text-xs opacity-80">Sales</p>
        <p className="font-bold text-lg">₹{payload[0].value}</p>
        <p className="text-xs opacity-80">{label}</p>
      </div>
    );
  }
  return null;
};
 
const SalesLineChart = ({ data = [], view, onViewChange, loading, percentageChange }) => {
  const totalSaleAmount = useMemo(() => {
    return data.reduce((sum, item) => sum + item.total, 0);
  }, [data]);
 
  const formattedTotal = new Intl.NumberFormat("en-IN").format(totalSaleAmount);
  // const percentageChange = data?.data?.sale_stats?.percentage_change;
 
  const yAxisConfig = useMemo(() => {
    if (!data.length) {
      return { domain: [0, 100], ticks: [0, 20, 40, 60, 80, 100] };
    }
 
    const maxValue = Math.max(...data.map((d) => d.total));
    let niceMax = Math.ceil((maxValue * 1.1) / 10) * 10;
    if (niceMax < 50) niceMax = 50;
 
    const step = niceMax / 8;
    const ticks = Array.from({ length: 9 }, (_, i) =>
      Math.round(i * step)
    );
 
    return { domain: [0, niceMax], ticks };
  }, [data]);
 
  if (loading) {
    return <div className="p-10 text-center">Loading chart...</div>;
  }
 
  return (
    <div className="bg-white rounded-xl p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-gray-500 text-sm">Total Sale Amount</p>   
          <h2 className="text-2xl font-bold">₹{formattedTotal}</h2>
               <p
            className={`text-sm ${
              Number(percentageChange) >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {percentageChange}%{" "}
            <span className="text-black">from last {view}</span>
          </p>
        </div>
 
        <div className="flex bg-gray-100 rounded-full p-1 text-sm">
          {["week", "month", "year"].map((btn) => (
            <button
              key={btn}
              onClick={() => onViewChange(btn)}
              className={`px-4 py-1 rounded-full capitalize transition-all ${
                view === btn
                  ? "bg-green-600 text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {btn}
            </button>
          ))}
        </div>
      </div>
 
      <div style={{ height: 420 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id={`line-${view}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#5AFFA3" />
                <stop offset="50%" stopColor="#5BC4FF" />
                <stop offset="100%" stopColor="#FF5BEF" />
              </linearGradient>
 
              <linearGradient id={`area-${view}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5AFFA3" stopOpacity={0.25} />
                <stop offset="50%" stopColor="#5BC4FF" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#FF5BEF" stopOpacity={0.15} />
              </linearGradient>
            </defs>
 
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
 
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6b7280", dy: 12, fontSize: 14 }}
            />
 
            <YAxis
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#6b7280", fontSize: 14 }}
            />
 
            <Tooltip content={<CustomTooltip />} />
 
            <Area
              type="monotone"
              dataKey="total"
              fill={`url(#area-${view})`}
              stroke="none"
            />
 
            <Area
              type="monotone"
              dataKey="total"
              stroke={`url(#line-${view})`}
              strokeWidth={4}
              fill="transparent"
              dot={{
                r: 6,
                fill: "#fff",
                strokeWidth: 3,
                stroke: `url(#line-${view})`,
              }}
              activeDot={{ r: 8, strokeWidth: 3 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
 
export default SalesLineChart;
 
 